import { __assign, __awaiter, __generator, __makeTemplateObject, __rest } from "tslib";
import React, { createContext, forwardRef, useContext, useLayoutEffect, useMemo, useRef, } from 'react';
import clsx from 'clsx';
import { ArrowDown2 } from '@ringcentral/juno-icon';
import { combineClasses, combineProps, omit, styled, useForkRef, usePrevious, useRetry, useSleep, useThemeProps, } from '../../../foundation';
import { RcBox } from '../../Box';
import { RcIconButton } from '../../Buttons/IconButton';
import { RcMenuItem } from '../../Menu/MenuItem';
import { menuListBoundaryPadding, StyledMenuListPadding, } from '../../VirtualizedMenu/styles';
import { modifyVlScrollerStyle, useDynamicHeight, useHighlightScroll, Virtuoso, } from '../../Virtuoso';
import { SuggestionListStyle } from './styles';
import { RcSuggestionListClasses } from './utils';
var SuggestionListContext = createContext(null);
var List = forwardRef(function (props, ref) {
    var listRef = useContext(SuggestionListContext);
    var forkRef = useForkRef(ref, listRef);
    return React.createElement("div", __assign({ ref: forkRef, "data-suggestion-list": true }, props));
});
var SuggestionList = forwardRef(function (inProps, ref) {
    var _a;
    var props = useThemeProps({ props: inProps, name: 'RcSuggestionList' });
    var highlightedIndex = props.highlightedIndex, options = props.options, getItemProps = props.getItemProps, _b = props.virtualize, virtualize = _b === void 0 ? true : _b, getMenuProps = props.getMenuProps, renderOption = props.renderOption, selectedOptions = props.value, inputValue = props.inputValue, groupVariant = props.groupVariant, groupExpanded = props.groupExpanded, renderGroup = props.renderGroup, optionsGroupList = props.optionsGroupList, getOptionDisabled = props.getOptionDisabled, MenuItem = props.MenuItem, changeHighlightedIndexReason = props.changeHighlightedIndexReason, isKeepHighlightedIndex = props.isKeepHighlightedIndex, componentsProp = props.components, onUpdatePopper = props.onUpdatePopper, getOptionLabel = props.getOptionLabel, padding = props.padding, _c = props.maxContainerHeight, maxContainerHeight = _c === void 0 ? '100%' : _c, classNameProp = props.className, classesProp = props.classes, _d = props.position, position = _d === void 0 ? 'absolute' : _d, selectedIndex = props.selectedIndex, rest = __rest(props, ["highlightedIndex", "options", "getItemProps", "virtualize", "getMenuProps", "renderOption", "value", "inputValue", "groupVariant", "groupExpanded", "renderGroup", "optionsGroupList", "getOptionDisabled", "MenuItem", "changeHighlightedIndexReason", "isKeepHighlightedIndex", "components", "onUpdatePopper", "getOptionLabel", "padding", "maxContainerHeight", "className", "classes", "position", "selectedIndex"]);
    var vlRef = useRef(null);
    var forkVlRef = useForkRef(ref, vlRef);
    var isTitleMode = groupVariant === 'normal';
    var listRef = useRef(null);
    var itemData = options;
    var itemCount = options.length;
    var classes = combineClasses(RcSuggestionListClasses, classesProp);
    var className = clsx(classNameProp, classes === null || classes === void 0 ? void 0 : classes.root);
    var _e = useDynamicHeight({
        itemCount: itemCount,
        maxContainerHeight: maxContainerHeight,
        onContainerHeightChange: function (changeHeight) {
            var scroller = scrollerRef.current;
            if (scroller === null || scroller === void 0 ? void 0 : scroller.style) {
                scroller.style.height = changeHeight + "px";
                onUpdatePopper === null || onUpdatePopper === void 0 ? void 0 : onUpdatePopper();
            }
        },
    }), totalListHeightChanged = _e.totalListHeightChanged, style = _e.style, containerHeighRef = _e.containerHeighRef;
    var sleep = useSleep().sleep;
    var scrollToIndexWithRetry = useRetry(function (location) { return __awaiter(void 0, void 0, void 0, function () {
        var toIndex, toElm_1, toElm;
        var _a, _b, _c;
        return __generator(this, function (_d) {
            switch (_d.label) {
                case 0:
                    toIndex = location.index;
                    if (!virtualize) {
                        toElm_1 = (_a = listRef.current) === null || _a === void 0 ? void 0 : _a.querySelector("[data-item-index=\"" + toIndex + "\"]");
                        toElm_1 === null || toElm_1 === void 0 ? void 0 : toElm_1.scrollIntoView({ block: 'nearest', inline: 'nearest' });
                        return [2 /*return*/, true];
                    }
                    if (location.index === 0 && padding !== undefined) {
                        location = __assign(__assign({}, location), { offset: -(typeof padding === 'number'
                                ? padding
                                : menuListBoundaryPadding) });
                    }
                    (_b = vlRef.current) === null || _b === void 0 ? void 0 : _b.scrollToIndex(location);
                    return [4 /*yield*/, sleep(0)];
                case 1:
                    _d.sent();
                    toElm = (_c = listRef.current) === null || _c === void 0 ? void 0 : _c.querySelector("[data-item-index=\"" + toIndex + "\"]");
                    if (toElm) {
                        return [2 /*return*/, true];
                    }
                    return [2 /*return*/, false];
            }
        });
    }); }, {
        retryTimes: 10,
        intervalTime: 20,
    }).retry;
    var _f = useHighlightScroll({
        containerHeighRef: containerHeighRef,
        scrollToIndex: scrollToIndexWithRetry,
    }), scrollerRef = _f.scrollerRef, scrollerRefFn = _f.scrollerRefFn, itemsRendered = _f.itemsRendered, scrollToHighlightedIndex = _f.scrollToHighlightedIndex;
    // for safari, prevent popover
    var virtuosoViewPort = (_a = scrollerRef.current) === null || _a === void 0 ? void 0 : _a.firstElementChild;
    useLayoutEffect(function () {
        if (virtuosoViewPort) {
            virtuosoViewPort.style.position = position;
        }
    }, [virtuosoViewPort, position]);
    var prevHighlightedIndex = usePrevious(function () { return highlightedIndex; }, true);
    useLayoutEffect(function () {
        if ((!virtualize || vlRef.current) &&
            !isKeepHighlightedIndex &&
            // * only scroll when reason is 'keyboard'
            changeHighlightedIndexReason &&
            changeHighlightedIndexReason !== 'mouse') {
            scrollToHighlightedIndex(prevHighlightedIndex, highlightedIndex, 
            // when title group topHighlightIndex to be 1, first item is group title
            isTitleMode ? 1 : 0);
        }
    });
    var handleScrolling = function (scrolling) {
        var list = listRef.current;
        if (list && list.style) {
            if (scrolling) {
                list.style.pointerEvents = 'none';
            }
            else {
                list.style.pointerEvents = '';
            }
        }
    };
    var itemContent = function (index, option) {
        var _a;
        var _b, _c, _d, _e;
        var currGroup = option.group;
        var isGroupTitle = option === (currGroup === null || currGroup === void 0 ? void 0 : currGroup.options[0]);
        var isFixedGroupExpanded = typeof groupExpanded === 'boolean';
        var expandIconProps = !isTitleMode && !isFixedGroupExpanded && isGroupTitle
            ? (_c = (_b = option.group) === null || _b === void 0 ? void 0 : _b.getExpandIconProps) === null || _c === void 0 ? void 0 : _c.call(_b, {
                className: clsx(classes.toggle, (_a = {},
                    _a[classes.expanded] = (_d = option.group) === null || _d === void 0 ? void 0 : _d.expanded,
                    _a)),
            }) : undefined;
        var itemProps = getItemProps({
            item: option,
            index: index,
            className: isGroupTitle ? classes.groupTitle : undefined,
        });
        var highlighted = highlightedIndex === index;
        var selected = selectedIndex === index;
        var resultProps = __assign(__assign(__assign({ 'data-item-index': !virtualize ? index : undefined }, option), itemProps), { key: itemProps.id });
        var indexInOwnGroup = resultProps.indexInOwnGroup;
        delete resultProps.indexInOwnGroup;
        // * when item is not disabled, that should check outside is that disabled
        if (!resultProps.freeSolo && !resultProps.disabled && getOptionDisabled) {
            resultProps.disabled = getOptionDisabled(option);
        }
        var state = {
            inputValue: inputValue,
            selected: selected,
            highlighted: highlighted,
            index: index,
            indexInOwnGroup: indexInOwnGroup,
        };
        if (renderGroup && isGroupTitle) {
            return renderGroup(resultProps, __assign(__assign({}, state), { expanded: (_e = option.group) === null || _e === void 0 ? void 0 : _e.expanded, expandIconProps: expandIconProps }));
        }
        if (renderOption) {
            // * as any for VirtualizedListWithAutoSizer type issue
            return renderOption(resultProps, state);
        }
        // TODO: that MenuItem will be remove
        if (MenuItem) {
            return (React.createElement(MenuItem
            // when not virtualize, use index as key
            , __assign({}, resultProps, { itemId: option.id, "data-suggestion-item-id": option.id, isHighlighted: selected || highlighted, isMember: option.isMember })));
        }
        return (React.createElement(RcMenuItem, __assign({ component: "div", selected: selected, focused: highlighted }, omit(resultProps, [
            'isSuggestion',
            'freeSolo',
            'label',
            'unSelectable',
        ])),
            getOptionLabel ? getOptionLabel(option) : option.label,
            React.createElement(RcBox, { flex: "1 1 auto" }),
            expandIconProps && (React.createElement(RcIconButton, __assign({}, expandIconProps, { symbol: ArrowDown2 })))));
    };
    var PaddingComponent = useMemo(function () {
        var paddingValue = padding !== undefined && itemCount > 0
            ? typeof padding === 'number'
                ? padding
                : menuListBoundaryPadding
            : 0;
        return function () { return React.createElement(StyledMenuListPadding, { height: paddingValue }); };
    }, [itemCount, padding]);
    var components = useMemo(function () {
        return __assign({ List: List, Header: PaddingComponent, Footer: PaddingComponent }, componentsProp);
    }, [componentsProp, PaddingComponent]);
    return (React.createElement(SuggestionListContext.Provider, { value: listRef }, virtualize ? (React.createElement(Virtuoso, __assign({ ref: forkVlRef, totalCount: itemCount, data: itemData, className: className, components: components, itemContent: itemContent }, getMenuProps(), combineProps({
        scrollerRef: function (scrollElm) {
            scrollerRefFn(scrollElm);
            modifyVlScrollerStyle(scrollElm, position);
        },
        itemsRendered: itemsRendered,
        totalListHeightChanged: totalListHeightChanged,
        style: style,
        isScrolling: handleScrolling,
    }, rest)))) : (React.createElement(List, { style: { maxHeight: style.height, overflow: 'auto' }, ref: function (scrollElm) {
            scrollerRefFn(scrollElm);
        } },
        React.createElement(PaddingComponent, null),
        options.map(function (x, i) {
            return itemContent(i, x);
        }),
        React.createElement(PaddingComponent, null)))));
});
export var RcSuggestionList = styled(SuggestionList)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  ", ";\n"], ["\n  ", ";\n"])), SuggestionListStyle);
RcSuggestionList.displayName = 'RcSuggestionList';
var templateObject_1;
